<template>
  <Layout class="layout--basic">
    <HeaderRoot title="Books" :hasDocSort="true" />
    <div class="layout__wrapper">
      <v-wait for="Error Loading Books">
        <template v-slot:waiting>
          <LoadingError />
        </template>
      </v-wait>
      <v-wait
        class="layout__wrapper-inner layout__wrapper-inner--small-gap container"
        for="Loading Books"
      >
        <template v-slot:waiting>
          <loading-spinner />
        </template>
        <section class="books-categories">
          <CategoriesSelection v-if="tagsView?.hasTags" :view="tagsView" />
        </section>

        <section class="docs-listing docs-listing--books">
          <ListDocs v-if="booksView" :view="booksView" />
        </section>
      </v-wait>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted, Raw, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import Layout from '@/app/components/Layout.vue'
import HeaderRoot from '@/app/components/header/Root.vue'
import CategoriesSelection from '@/app/components/listing/categories/CategoriesSelection.vue'
import ListDocs from '@/app/components/listing/ListDocs.vue'

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'

import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'

import {
  BooksView,
  showReviewPrompt,
  trustpilotReviewPrompt,
} from '@/models/doc.book'
import LoadingError from '@/components/ui/LoadingError.vue'
import { DocsTagsView } from '@/models/docs.tags'
import { offline } from '@/services/offline'
import { loadBookListFromNativeStorage } from '@/services/native.storage'
import { ListView } from '@/models/docs.list'

const router = useRouter()
const wait = useWait()

const booksView = ref<Raw<ListView> | null>(null)
const tagsView = ref<Raw<DocsTagsView> | null>(null)

onBeforeMount(async () => {
  await loadBooks()
})

onMounted(async () => {
  // Check if we need to show the App Review Prompt
  // The trigger is going back to Books from reading a book. (#2812)
  const queryFrom = router.currentRoute.value.query.from
  if (queryFrom === 'book.page') {
    await showReviewPrompt()
    await trustpilotReviewPrompt()
  }
})

async function loadBooks(): Promise<void> {
  wait.start('Loading Books')
  try {
    if (offline.isOffline) {
      await loadBookListFromNativeStorage()
    }

    const response = await backend.getBooksWithRecommendations()

    const books = await BooksView.createView(
      response,
      undefined,
      true,
      undefined,
      response.tags,
    )

    booksView.value = new ListView('All Books', books)

    tagsView.value = new DocsTagsView(response.tags, 'books')
    wait.end('Loading Books')
  } catch (error: any) {
    wait.start('Error Loading Books')
    Sentry.captureException(error)
  }
}
</script>
